import { choiceAnswer, contractField, settledAnswer } from './contract'
import type { PolicyDocView } from './types'

/* A lookback node implements scoping's lookback answer (D-N2): its D4–D6
   Lookback settings read scoping's rows unless design writes its own, and
   one design writes that disagrees is reported, never silently kept.

   ponytail: every decision node reads the scoping lookback, not only the
   ones that look back. Ceiling: a code-check node shows a lookback it never
   applies. Upgrade path: a lookback node type, once the engine names one.
   `history-claim-type-matching` has no D4–D6 counterpart (it's a filter
   step, D2), so it isn't prefilled. */

/** D6's option for scoping's "Lookback includes same day": the line under
 *  review sits on the window's last day. */
const SAME_DAY_ENDS: Record<string, string> = {
  yes: 'Both ends',
  no: 'First day only',
}

/** Scoping's lookback answers, as the D4–D6 values a lookback node carries;
 *  a row scoping left open has no key. */
export const scopingLookback = (
  answers: PolicyDocView['answers'],
): Partial<Record<'D4' | 'D6', string>> => {
  const lookback: Partial<Record<'D4' | 'D6', string>> = {}
  const period = settledAnswer(
    contractField('period-basis')!,
    answers['period-basis'],
  )
  if (!period.muted) lookback.D4 = period.text
  const sameDay =
    SAME_DAY_ENDS[
      choiceAnswer(
        answers['lookback-includes-same-day'],
        contractField('lookback-includes-same-day'),
      )?.choice ?? ''
    ]
  if (sameDay) lookback.D6 = sameDay
  return lookback
}

export interface LookbackDrift {
  nodeId: string
  nodeLabel: string
  settingId: 'D4' | 'D6'
  scoping: string
  design: string
}

/** Every Lookback setting design wrote that differs from scoping's answer. */
export const lookbackDrift = (
  view: Pick<PolicyDocView, 'answers' | 'nodes'>,
): LookbackDrift[] => {
  const scoping = scopingLookback(view.answers)
  return view.nodes.flatMap((node) =>
    (['D4', 'D6'] as const).flatMap((settingId) => {
      const design = node.settings?.[settingId]?.value.trim()
      const expected = scoping[settingId]
      return design && expected && design !== expected
        ? [
            {
              nodeId: node.id,
              nodeLabel: node.label,
              settingId,
              scoping: expected,
              design,
            },
          ]
        : []
    }),
  )
}
